import { computed, type MaybeRef } from 'vue';
import dayjs from 'dayjs';
import { flatMap, isNil } from 'lodash-es';
import { freedomApp, freedomPayment, flightEntity } from 'freedom-shared';
import type { Airflow, Payments } from '@aviata/platform-types';

import { useBill } from '@/entities/bill';
import { useOrder } from '@/entities/order';

import { analyticsEventsLib } from '@/shared/lib';
import { api } from '@/shared/api';
import { appConfig } from '@/shared/config';

// Поля FC (flights connect) отсутствуют в @aviata/platform-types — TODO: убрать после обновления platform-types
type FlightWithConnectIdx = Airflow.Offer.Flight & {
  connected_flight_idx?: number | null;
};

type SegmentWithConnectIdx = Airflow.Segment.Entity & {
  connected_segment_idx?: number | null;
};

export function usePayment({ orderId }: { orderId: MaybeRef<string> }) {
  const {
    order,
    error: orderError,
    isLoading: isOrderLoading,
    isError: isOrderError,
    isNew: isOrderNew,
    isIssued: isOrderIssued,
    isCanceled: isOrderCancelled,
    isInProgress: isOrderInProgress,
    refetch: refetchOrder,
  } = useOrder({ orderId });

  const {
    bill,
    error: billError,
    isLoading: isBillLoading,
    isError: isBillError,
    isPaid: isBillPaid,
    isExpired: isBillExpired,
    refetch: refetchBill,
  } = useBill({
    orderId: computed(() => order.value?.id),
  });

  const error = computed(() => orderError.value || billError.value);

  const expiresAt = computed(() => {
    const expiresIn = bill.value?.expires_in;
    if (!isNil(expiresIn)) {
      return dayjs().add(expiresIn, 'seconds').toString();
    }
    return null;
  });

  const products = computed(() => order.value?.products ?? []);

  const bookingProducts = computed(
    () =>
      products.value
        .filter(item => item.type === 'airflow.booking')
        .map(booking => booking.data) as Airflow.Product.BookingData[],
  );

  const dict = computed(() => bookingProducts.value[0]?.dict ?? null);

  const mappedFlights = computed(() => {
    if (bookingProducts.value.length === 0 || isNil(dict.value)) {
      return null;
    }

    const allFlights = flatMap(
      bookingProducts.value,
      booking => booking.offer.flights as FlightWithConnectIdx[],
    );

    const hasConnectedFlights = allFlights.some(
      flight => !isNil(flight.connected_flight_idx),
    );

    if (!hasConnectedFlights) {
      // Обычный OW или RT — ГДС сама собрала рейсы, ничего не делаем
      // TODO: Update platform-types in the root node_modules
      return allFlights.map(flight =>
        flightEntity.lib.mapFlight(dict.value!, flight),
      );
    }

    // FC (flights connect): группируем по connected_flight_idx
    // Одна группа = один перелет; VI → 1 группа (OW), OWC → 2 группы (RT)
    const groups = new Map<number, FlightWithConnectIdx[]>();
    for (const flight of allFlights) {
      const idx = flight.connected_flight_idx ?? 0;
      if (!groups.has(idx)) {
        groups.set(idx, []);
      }
      groups.get(idx)!.push(flight);
    }

    return [...groups.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, groupFlights]) => {
        const sortedFlights = [...groupFlights].sort(
          (a, b) =>
            ((a.segments[0] as SegmentWithConnectIdx)?.connected_segment_idx ??
              0) -
            ((b.segments[0] as SegmentWithConnectIdx)?.connected_segment_idx ??
              0),
        );
        const mergedFlight: Airflow.Offer.Flight = {
          ...sortedFlights[0],
          duration: sortedFlights.reduce((sum, f) => sum + f.duration, 0),
          segments: flatMap(sortedFlights, f => f.segments),
        };
        return flightEntity.lib.mapFlight(dict.value!, mergedFlight);
      });
  });

  const passengers = computed(() => bookingProducts.value[0]?.passengers ?? []);

  const isLoading = computed(() => {
    return isOrderLoading.value || isBillLoading.value;
  });

  const isError = computed(() => {
    return isOrderError.value || isBillError.value;
  });

  function startPayment() {
    if (isNil(order.value) || isNil(bill.value)) {
      return Promise.reject(new Error('ERR_BILL_OR_ORDER_IS_UNDEFINED'));
    }

    const promocodeModifier = bill.value.dynamic_modifiers?.find(
      modifier =>
        (modifier.modifier_type as  // TODO: Add to aviatakz libs
          | Payments.Bill.DynamicModifier['modifier_type']
          | 'PROMO_CODE') === 'PROMO_CODE',
    );

    // Send analytics event when button is clicked, before payment attempt
    // This ensures we track all button clicks, even if payment fails
    analyticsEventsLib.payButtonClick({
      orderId: order.value.id,
      order: order.value,
      bill: bill.value,
      promocodeModifier,
      promocodeNumber: promocodeModifier?.value ?? null,
    });

    return freedomPayment.model.useFreedomPayment({
      api,
      billId: bill.value.id,
      isDevRuntime: appConfig.isDevRuntimeEnv,
      prodPay: freedomApp.lib.avia.payByBillId,
    }).pay;
  }

  return {
    order,
    bill,
    dict,
    error,
    expiresAt,
    passengers,
    mappedFlights,
    bookingProducts,
    isError,
    isLoading,
    isOrderNew,
    isOrderCancelled,
    isOrderInProgress,
    isBillExpired,
    isOrderIssued,
    isBillPaid,
    refetchBill,
    refetchOrder,
    startPayment,
  };
}
